import PropTypes from 'prop-types';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2,
  Users,
  MapPin,
  Mail,
  Phone,
  Link as LinkIcon,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Calendar,
  Clock,
  Tag,
} from 'lucide-react';
import AppLayout from '../../src/components/AppLayout';
import VenueChatbot from '../../src/components/VenueChatbot';
import '../styles/venue-detail.css';
import { API_BASE_URL } from '../../src/config';
import { resolveBackendAssetUrl } from '../../src/utils/assetUrl';
import { getMemberAuthToken, getMemberAuthUserId } from '../../src/utils/memberAuth';

const resolveImageSrc = (value) => {
  return resolveBackendAssetUrl(value, { apiBaseUrl: API_BASE_URL });
};

const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 8; hour <= 22; hour++) {
    const period = hour < 12 ? 'AM' : 'PM';
    const displayHour = hour > 12 ? hour - 12 : hour;
    const timeString = `${String(hour).padStart(2, '0')}:00:00`;
    slots.push({
      value: timeString,
      label: `${displayHour}:00 ${period}`,
    });
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();
const BOOKING_DURATION_OPTIONS = [6, 12, 24];
const OVERLAP_ERROR_MESSAGE = 'Selected time overlaps with an existing booking or preparation window. Please choose another time.';

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const toLocalIsoDate = (dateValue) => {
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const day = String(dateValue.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateInput = (value) => {
  if (!value) return null;

  const rawValue = String(value).trim();
  if (!rawValue) return null;

  if (DATE_ONLY_REGEX.test(rawValue)) {
    const [year, month, day] = rawValue.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(rawValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const addDaysToIsoDate = (isoDate, offsetDays) => {
  const baseDate = parseDateInput(isoDate);
  if (!baseDate || !Number.isFinite(offsetDays)) return isoDate;
  baseDate.setDate(baseDate.getDate() + offsetDays);
  return toLocalIsoDate(baseDate);
};

export default function VenueDetailPage({ user, onLogout }) {
  const { venueId } = useParams();
  const navigate = useNavigate();

  const isVerified = user?.isApproved === 'active' || user?.isApproved === 'approved' || user?.isApproved === true || user?.isApproved === 1 || user?.isApproved === '1';
  const isIncomplete = user?.isApproved === 'incomplete';
  const effectiveMemberUserId = getMemberAuthUserId() || Number(user?.id || 0) || null;

  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [bookingForm, setBookingForm] = useState({
    customerName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
    email: user?.email || '',
    mobileNumber: user?.phone || '',
    bookingDate: '',
    startTime: '',
    endTime: '',
  });

  useEffect(() => {
    const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || (user?.name || '').trim();
    setBookingForm((prev) => ({
      ...prev,
      customerName: fullName || prev.customerName,
      email: user?.email || prev.email,
      mobileNumber: user?.phone || user?.mobileNumber || prev.mobileNumber,
    }));
  }, [user?.firstName, user?.lastName, user?.name, user?.email, user?.phone, user?.mobileNumber]);

  const [availability, setAvailability] = useState([]);
  const [nextDayAvailability, setNextDayAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [selectedDurationHours, setSelectedDurationHours] = useState(null);
  const [endDayOffset, setEndDayOffset] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const todayDate = new Date();
    return new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);
  });
  const submitLockRef = useRef(false);
  const calendarPickerRef = useRef(null);

  useEffect(() => {
    const fetchVenueDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/api/venues/${venueId}`);
        if (!response.ok) {
          throw new Error('Failed to load venue details');
        }
        const data = await response.json();
        setVenue(data.venue);
      } catch (err) {
        console.error('Error fetching venue:', err);
        setError('Unable to load venue details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (venueId) {
      fetchVenueDetail();
    }
  }, [venueId]);

  useEffect(() => {
    const fetchAvailability = async () => {
      if (!bookingForm.bookingDate || !venueId) return;

      setLoadingAvailability(true);
      try {
        const nextDayDate = addDaysToIsoDate(bookingForm.bookingDate, 1);
        const [todayResponse, nextDayResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/venues/${venueId}/availability?date=${bookingForm.bookingDate}`),
          fetch(`${API_BASE_URL}/api/venues/${venueId}/availability?date=${nextDayDate}`),
        ]);

        if (!todayResponse.ok || !nextDayResponse.ok) {
          throw new Error('Failed to check availability');
        }

        const [todayData, nextDayData] = await Promise.all([
          todayResponse.json(),
          nextDayResponse.json(),
        ]);

        setAvailability(todayData.occupiedSlots || []);
        setNextDayAvailability(nextDayData.occupiedSlots || []);
      } catch (err) {
        console.error('Error fetching availability:', err);
        setAvailability([]);
        setNextDayAvailability([]);
      } finally {
        setLoadingAvailability(false);
      }
    };

    fetchAvailability();
  }, [bookingForm.bookingDate, venueId]);

  useEffect(() => {
    if (!showConfirmModal) {
      document.body.style.overflow = '';
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showConfirmModal]);

  useEffect(() => {
    if (!showConfirmModal) return undefined;

    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      if (submitting) return;

      setShowConfirmModal(false);
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showConfirmModal, submitting]);

  useEffect(() => {
    if (!isCalendarOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (calendarPickerRef.current && !calendarPickerRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isCalendarOpen]);

  const isSlotOccupied = (slotValue) => {
    if (availability.length === 0) return false;

    return availability.some((occupied) => {
      const slotTime = slotValue;
      return slotTime >= occupied.startTime && slotTime < occupied.endTime;
    });
  };

  const hasDurationOverlapFromSlot = (slotValue, durationHoursValue) => {
    if (!slotValue || !Number.isFinite(durationHoursValue)) return false;
    if (isSlotOccupied(slotValue)) return true;

    const computedRange = calculateEndTimeFromDuration(slotValue, durationHoursValue);
    if (!computedRange) return true;

    const { endTime: computedEndTime, dayOffset: computedEndDayOffset } = computedRange;

    const hasSameDayOverlap = TIME_SLOTS.some((slot) => {
      if (computedEndDayOffset === 0) {
        if (slot.value < slotValue || slot.value >= computedEndTime) {
          return false;
        }
        return isSlotOccupied(slot.value);
      }

      if (slot.value < slotValue) {
        return false;
      }

      return isSlotOccupied(slot.value);
    });

    const hasNextDayOverlap = computedEndDayOffset > 0 && TIME_SLOTS.some((slot) => {
      if (slot.value >= computedEndTime) {
        return false;
      }

      return isSlotOccupiedNextDay(slot.value);
    });

    return hasSameDayOverlap || hasNextDayOverlap;
  };

  const bookingIdentity = useMemo(() => {
    const fullNameFromUser = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || (user?.name || '').trim();
    return {
      fullName: fullNameFromUser || bookingForm.customerName.trim(),
      email: (user?.email || bookingForm.email || '').trim(),
      mobileNumber: (user?.phone || user?.mobileNumber || bookingForm.mobileNumber || '').trim(),
    };
  }, [bookingForm.customerName, bookingForm.email, bookingForm.mobileNumber, user?.email, user?.firstName, user?.lastName, user?.name, user?.phone, user?.mobileNumber]);

  const validateBooking = () => {
    if (!bookingIdentity.fullName) {
      return 'Please enter your name';
    }
    if (!bookingForm.bookingDate) {
      return 'Please select a booking date';
    }
    if (!bookingForm.startTime) {
      return 'Please select a start time';
    }
    if (!selectedDurationHours) {
      return 'Please choose how many hours you want to book';
    }
    if (!bookingForm.endTime) {
      return 'Please select an end time';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = parseDateInput(bookingForm.bookingDate);
    if (!selectedDate) {
      return 'Please select a valid booking date';
    }
    selectedDate.setHours(0, 0, 0, 0);
    if (selectedDate < today) {
      return 'Cannot book dates in the past';
    }

    const computedDurationHours = resolveDurationHours(bookingForm.startTime, bookingForm.endTime, endDayOffset);
    const actualDuration = computedDurationHours;
    if (actualDuration !== selectedDurationHours) {
      return `Selected time range does not match the ${selectedDurationHours}-hour duration`;
    }

    const hasSameDayOverlap = TIME_SLOTS.some((slot) => {
      if (!bookingForm.startTime) return false;

      if (endDayOffset === 0) {
        if (slot.value < bookingForm.startTime || slot.value >= bookingForm.endTime) {
          return false;
        }
        return isSlotOccupied(slot.value);
      }

      return slot.value >= bookingForm.startTime && isSlotOccupied(slot.value);
    });

    const hasNextDayOverlap = endDayOffset > 0 && TIME_SLOTS.some((slot) => {
      if (slot.value >= bookingForm.endTime) {
        return false;
      }
      return isSlotOccupiedNextDay(slot.value);
    });

    if (hasSameDayOverlap || hasNextDayOverlap) {
      return OVERLAP_ERROR_MESSAGE;
    }

    if (isSlotOccupied(bookingForm.startTime)) {
      return 'Selected start time is not available';
    }

    if (endDayOffset === 0) {
      const endTimeCheck = TIME_SLOTS.find((s) => s.value === bookingForm.endTime);
      if (endTimeCheck) {
        const hourBefore = parseInt(bookingForm.endTime.split(':')[0], 10) - 1;
        const checkTime = `${String(hourBefore).padStart(2, '0')}:00:00`;
        if (isSlotOccupied(checkTime)) {
          return 'Selected end time overlaps with an existing booking';
        }
      }
    } else {
      const nextDayHourBefore = parseInt(bookingForm.endTime.split(':')[0], 10) - 1;
      const nextDayCheckTime = `${String((nextDayHourBefore + 24) % 24).padStart(2, '0')}:00:00`;
      if (isSlotOccupiedNextDay(nextDayCheckTime)) {
        return 'Selected end time overlaps with an existing booking';
      }
    }

    return null;
  };

  const parseTimeToMinutes = (value) => {
    if (!value) return null;
    const [hours, minutes] = value.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    return (hours * 60) + minutes;
  };

  const parseMinutesToTime = (totalMinutes) => {
    if (!Number.isFinite(totalMinutes) || totalMinutes < 0 || totalMinutes >= (24 * 60)) {
      return null;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
  };

  const resolveDurationHours = (startTimeValue, endTimeValue, endDateOffsetValue = 0) => {
    const startMinutesValue = parseTimeToMinutes(startTimeValue);
    const endMinutesValue = parseTimeToMinutes(endTimeValue);
    if (!Number.isFinite(startMinutesValue) || !Number.isFinite(endMinutesValue)) return 0;

    const normalizedOffset = Number.isFinite(endDateOffsetValue) ? endDateOffsetValue : 0;
    const totalEndMinutes = endMinutesValue + (normalizedOffset * 24 * 60);
    const deltaMinutes = totalEndMinutes - startMinutesValue;
    if (deltaMinutes <= 0) return 0;
    return deltaMinutes / 60;
  };

  const calculateEndTimeFromDuration = (startTime, durationHoursValue) => {
    const startMinutesValue = parseTimeToMinutes(startTime);
    if (!Number.isFinite(startMinutesValue) || !Number.isFinite(durationHoursValue)) {
      return null;
    }

    const durationMinutes = durationHoursValue * 60;
    const totalMinutes = startMinutesValue + durationMinutes;
    const normalizedMinutes = totalMinutes % (24 * 60);
    const computedDayOffset = Math.floor(totalMinutes / (24 * 60));
    const computedEndTime = parseMinutesToTime(normalizedMinutes);

    if (!computedEndTime) {
      return null;
    }

    return {
      endTime: computedEndTime,
      dayOffset: computedDayOffset,
    };
  };

  const formatTime = (value) => {
    if (!value) return '—';
    const [hoursText, minutesText = '00'] = value.split(':');
    const hours = Number(hoursText);
    if (Number.isNaN(hours)) return value;
    const period = hours < 12 ? 'AM' : 'PM';
    const normalizedHour = hours % 12 || 12;
    return `${normalizedHour}:${minutesText} ${period}`;
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const date = parseDateInput(value);
    if (!date || Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-PH', {
      weekday: 'short',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (value) => {
    return `₱${Number(value || 0).toLocaleString('en-PH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const durationHours = selectedDurationHours || resolveDurationHours(bookingForm.startTime, bookingForm.endTime, endDayOffset);
  // Package pricing helpers
  const price6hrs = Number(venue?.price6hrs || 0);
  const price12hrs = Number(venue?.price12hrs || 0);
  const price24hrs = Number(venue?.price24hrs || 0);
  const discountPct = Number(venue?.discountPercentage || 0);
  const applyDiscount = (price) => price - (price * discountPct / 100);

  const resolvePackagePrice = (hours) => {
    if (hours <= 6 && price6hrs > 0) return { packageHours: 6, originalPrice: price6hrs };
    if (hours <= 12 && price12hrs > 0) return { packageHours: 12, originalPrice: price12hrs };
    if (price24hrs > 0) return { packageHours: 24, originalPrice: price24hrs };
    if (price12hrs > 0) return { packageHours: 12, originalPrice: price12hrs };
    if (price6hrs > 0) return { packageHours: 6, originalPrice: price6hrs };
    return { packageHours: null, originalPrice: 0 };
  };

  // Determine estimated cost based on duration and nearest package
  const { packageHours: estimatedPackageHours, originalPrice: estimatedOriginalCost } = resolvePackagePrice(durationHours);
  const estimatedTotalCost = applyDiscount(estimatedOriginalCost);
  const todayDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const calendarDays = useMemo(() => {
    const monthStart = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const gridStart = new Date(monthStart);
    gridStart.setDate(monthStart.getDate() - monthStart.getDay());

    return Array.from({ length: 42 }, (_, index) => {
      const current = new Date(gridStart);
      current.setDate(gridStart.getDate() + index);
      current.setHours(0, 0, 0, 0);
      const iso = toLocalIsoDate(current);

      return {
        iso,
        day: current.getDate(),
        isCurrentMonth: current.getMonth() === calendarMonth.getMonth(),
        isPast: current < todayDate,
        isToday: current.getTime() === todayDate.getTime(),
        isSelected: bookingForm.bookingDate === iso,
      };
    });
  }, [bookingForm.bookingDate, calendarMonth, todayDate]);

  const bookingDateLabel = bookingForm.bookingDate
    ? parseDateInput(bookingForm.bookingDate)?.toLocaleDateString('en-PH', {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }) || 'Select booking date'
    : 'Select booking date';

  const handleBookingDateSelect = (selectedDate) => {
    setBookingForm((prev) => ({
      ...prev,
      bookingDate: selectedDate,
      startTime: '',
      endTime: '',
    }));
    setSelectedDurationHours(null);
    setEndDayOffset(0);
    setBookingError('');
    setIsCalendarOpen(false);
  };

  const handleDurationSelection = (durationHoursValue) => {
    setSelectedDurationHours(durationHoursValue);
    setBookingForm((prev) => ({
      ...prev,
      startTime: '',
      endTime: '',
    }));
    setEndDayOffset(0);
    setBookingError('');
  };

  const isSlotOccupiedNextDay = (slotValue) => {
    if (nextDayAvailability.length === 0) return false;

    return nextDayAvailability.some((occupied) => {
      const slotTime = slotValue;
      return slotTime >= occupied.startTime && slotTime < occupied.endTime;
    });
  };

  const handleTimeSlotSelection = (slotValue) => {
    if (!selectedDurationHours) {
      setBookingError('Please choose how many hours first.');
      return;
    }

    if (hasDurationOverlapFromSlot(slotValue, selectedDurationHours)) {
      setBookingError(OVERLAP_ERROR_MESSAGE);
      return;
    }

    const computedRange = calculateEndTimeFromDuration(slotValue, selectedDurationHours);
    if (!computedRange) {
      setBookingError('Unable to compute end time for selected duration.');
      return;
    }

    const { endTime: computedEndTime, dayOffset: computedEndDayOffset } = computedRange;

    const isSameSelection =
      bookingForm.startTime === slotValue
      && bookingForm.endTime === computedEndTime
      && endDayOffset === computedEndDayOffset;

    if (isSameSelection) {
      setBookingForm((prev) => ({ ...prev, startTime: '', endTime: '' }));
      setEndDayOffset(0);
      setBookingError('');
      return;
    }

    setBookingForm((prev) => {
      return { ...prev, startTime: slotValue, endTime: computedEndTime };
    });
    setEndDayOffset(computedEndDayOffset);

    setBookingError('');
  };

  const estimatedPackageLabel = (() => {
    if (durationHours <= 0) return '—';
    if (estimatedPackageHours) return `${estimatedPackageHours} Hours Package`;
    return 'No package configured';
  })();

  const confirmationDetails = [
    { label: 'Venue Name', value: venue?.name || '—' },
    { label: 'Booking Date', value: formatDate(bookingForm.bookingDate) },
    { label: 'Start Time', value: formatTime(bookingForm.startTime) },
    {
      label: 'End Time',
      value: bookingForm.endTime
        ? `${formatTime(bookingForm.endTime)}${endDayOffset > 0 ? ` (next day)` : ''}`
        : '—',
    },
    {
      label: 'End Date',
      value: bookingForm.bookingDate
        ? formatDate(addDaysToIsoDate(bookingForm.bookingDate, endDayOffset))
        : '—',
    },
    { label: 'Duration', value: durationHours > 0 ? `${durationHours} hour${durationHours === 1 ? '' : 's'}` : '—' },
    { label: 'Package', value: estimatedPackageLabel },
    { label: 'Full Name', value: bookingIdentity.fullName || '—' },
    { label: 'Email', value: bookingIdentity.email || '—' },
    { label: 'Mobile Number', value: bookingIdentity.mobileNumber || '—' },
  ];

  const startBookingCheckout = async (bookingId) => {
    const token = getMemberAuthToken();
    const authHeaders = token
      ? { Authorization: `Bearer ${token}` }
      : {};

    const response = await fetch(`${API_BASE_URL}/api/payments/checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({
        paymentFor: 'venue_booking',
        referenceId: bookingId,
        userId: effectiveMemberUserId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Unable to start secure checkout.');
    }

    const data = await response.json();
    if (!data?.checkoutUrl) {
      throw new Error('Secure checkout URL is missing.');
    }

    window.location.assign(data.checkoutUrl);
  };

  const submitBookingRequest = async () => {
    if (submitLockRef.current) {
      return;
    }
    if (!effectiveMemberUserId) {
      setBookingError('Unable to resolve your member session. Please log in again.');
      return;
    }

    submitLockRef.current = true;
    setSubmitting(true);
    try {
      const payload = {
        userId: effectiveMemberUserId,
        customerName: bookingIdentity.fullName,
        bookingDate: bookingForm.bookingDate,
        startTime: bookingForm.startTime,
        endTime: bookingForm.endTime,
        mobileNumber: bookingIdentity.mobileNumber || null,
        email: bookingIdentity.email || null,
        pricing: {
          packageHours: estimatedPackageHours,
          packageLabel: estimatedPackageLabel,
          discountPercentage: discountPct,
          originalPricePhp: Number(estimatedOriginalCost.toFixed(2)),
          finalPricePhp: Number(estimatedTotalCost.toFixed(2)),
        },
      };

      const response = await fetch(`${API_BASE_URL}/api/venues/${venueId}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit booking request');
      }

      const responseData = await response.json();
      const bookingId = Number(responseData?.booking?.id);
      if (!Number.isFinite(bookingId)) {
        throw new Error('Booking was saved, but no booking id was returned for payment.');
      }

      setSubmitted(true);
      await startBookingCheckout(bookingId);
    } catch (err) {
      console.error('Error submitting booking:', err);
      setBookingError(err.message || 'Unable to submit booking. Please try again.');
    } finally {
      setSubmitting(false);
      submitLockRef.current = false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current || submitting) {
      return;
    }
    setBookingError('');

    const validationError = validateBooking();
    if (validationError) {
      setBookingError(validationError);
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmBooking = async () => {
    if (submitLockRef.current || submitting) {
      return;
    }
    setShowConfirmModal(false);
    await submitBookingRequest();
  };

  const handlePrevImage = () => {
    if (!venue?.images || venue.images.length === 0) return;
    setCurrentImageIndex((prev) => (prev === 0 ? venue.images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    if (!venue?.images || venue.images.length === 0) return;
    setCurrentImageIndex((prev) => (prev === venue.images.length - 1 ? 0 : prev + 1));
  };

  if (loading) {
    return (
      <AppLayout title="Venue Booking" user={user} onLogout={onLogout}>
        <div className="venue-detail-page">
          <div className="venue-detail-page__loading">
            <p>Loading venue details...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error || !venue) {
    return (
      <AppLayout title="Venue Booking" user={user} onLogout={onLogout}>
        <div className="venue-detail-page">
          <div className="venue-detail-page__error">
            <AlertCircle size={48} />
            <p>{error || 'Venue not found'}</p>
            <button
              type="button"
              className="venue-detail-page__back-button"
              onClick={() => navigate('/venues')}
            >
              Back to Venues
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const currentImage = venue.images && venue.images.length > 0 ? venue.images[currentImageIndex] : null;

  if (!isVerified) {
    return (
      <AppLayout title="Venue Booking" user={user} onLogout={onLogout}>
        <div className="venue-detail-page">
          <button
            type="button"
            className="venue-detail-page__back"
            onClick={() => navigate('/venues')}
          >
            <ChevronLeft size={20} />
            Back to Venues
          </button>

          <div className="verification-prompt" style={{
            padding: '4rem 2rem',
            textAlign: 'center',
            maxWidth: '600px',
            margin: '2rem auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1.5rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ color: '#f59e0b' }}>
              <AlertCircle size={48} />
            </div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
              {isIncomplete ? "Verification Required" : "Application Under Review"}
            </h2>
            <p style={{ color: '#4b5563', lineHeight: '1.6' }}>
              {isIncomplete
                ? "Your membership profile is incomplete. Please verify your details to access venue booking."
                : "Your membership application is still under review. Once approved, you will gain access to venue booking."}
            </p>
            <div style={{ display: 'flex', gap: '1rem' }}>
              {isIncomplete ? (
                <button
                  type="button"
                  onClick={() => navigate('/complete-profile')}
                  style={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.375rem',
                    border: 'none',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Complete Verification
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/membership-form')}
                  style={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.375rem',
                    border: 'none',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  View Application
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                style={{
                  backgroundColor: 'white',
                  color: '#374151',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.375rem',
                  border: '1px solid #d1d5db',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Venue Booking" user={user} onLogout={onLogout}>
      <div className="venue-detail-page">
        <button
          type="button"
          className="venue-detail-page__back"
          onClick={() => navigate('/venues')}
        >
          <ChevronLeft size={20} />
          Back to Venues
        </button>

        <div className="venue-detail-page__main">
          <section className="venue-detail-page__details">
            {/* Image Carousel */}
            <section className="venue-detail-page__carousel">
              {currentImage ? (
                <>
                  <img
                    src={resolveImageSrc(currentImage.url)}
                    alt={venue.name}
                    className="venue-detail-page__carousel-image"
                  />
                  {venue.images.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="venue-detail-page__carousel-nav venue-detail-page__carousel-nav--prev"
                        onClick={handlePrevImage}
                        aria-label="Previous image"
                      >
                        <ChevronLeft size={24} />
                      </button>
                      <button
                        type="button"
                        className="venue-detail-page__carousel-nav venue-detail-page__carousel-nav--next"
                        onClick={handleNextImage}
                        aria-label="Next image"
                      >
                        <ChevronRight size={24} />
                      </button>
                      <div className="venue-detail-page__carousel-indicators">
                        {venue.images.map((_, index) => (
                          <button
                            key={index}
                            type="button"
                            className={`venue-detail-page__carousel-indicator ${
                              index === currentImageIndex ? 'active' : ''
                            }`}
                            onClick={() => setCurrentImageIndex(index)}
                            aria-label={`Go to image ${index + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="venue-detail-page__carousel-placeholder">
                  <Building2 size={64} />
                  <p>No images available</p>
                </div>
              )}
            </section>

            {/* Venue Information */}
            <section className="venue-detail-page__info">
            <h1 className="venue-detail-page__title">{venue.name}</h1>

            <div className="venue-detail-page__meta">
              <span className="venue-detail-page__meta-item">
                <Building2 size={16} />
                {venue.type}
              </span>
              <span className="venue-detail-page__meta-item">
                <Users size={16} />
                {venue.maxPax || venue.capacity} guests
              </span>
            </div>

            {/* Package Pricing Cards */}
            <div className="venue-detail-page__pricing-section">
              {discountPct > 0 && (
                <span className="venue-detail-page__discount-badge">
                  <Tag size={14} />
                  {discountPct}% OFF
                </span>
              )}
              <div className="venue-detail-page__pricing-grid">
                {[
                  { label: '6 Hours', price: price6hrs },
                  { label: '12 Hours', price: price12hrs },
                  { label: '24 Hours', price: price24hrs },
                ].map(({ label, price }) => {
                  const discounted = applyDiscount(price);
                  return (
                    <div key={label} className="venue-detail-page__pricing-card">
                      <span className="venue-detail-page__pricing-label">{label}</span>
                      {discountPct > 0 && price > 0 ? (
                        <>
                          <span className="venue-detail-page__pricing-original">
                            ₱{price.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                          </span>
                          <span className="venue-detail-page__pricing-final">
                            ₱{discounted.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                          </span>
                        </>
                      ) : (
                        <span className="venue-detail-page__pricing-value">
                          ₱{price.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="venue-detail-page__description">
              <h3>About This Venue</h3>
              {venue.detailedDescription ? (
                <p>{venue.detailedDescription}</p>
              ) : (
                <p className="venue-detail-page__empty-text">No detailed description available.</p>
              )}
            </div>

            {venue.amenities && (
              <div className="venue-detail-page__amenities">
                <h3>Amenities</h3>
                <ul>
                  {venue.amenities.split(',').map((amenity, index) => (
                    <li key={index}>{amenity.trim()}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="venue-detail-page__contact">
              <h3>Contact & Location</h3>
              <div className="venue-detail-page__contact-grid">
                {venue.location && (
                  <div className="venue-detail-page__contact-item">
                    <MapPin size={18} />
                    <span>{venue.location}</span>
                  </div>
                )}
                {venue.email && (
                  <div className="venue-detail-page__contact-item">
                    <Mail size={18} />
                    <a href={`mailto:${venue.email}`}>{venue.email}</a>
                  </div>
                )}
                {venue.mobileNumber && (
                  <div className="venue-detail-page__contact-item">
                    <Phone size={18} />
                    <a href={`tel:${venue.mobileNumber}`}>{venue.mobileNumber}</a>
                  </div>
                )}
                {venue.link && (
                  <div className="venue-detail-page__contact-item">
                    <LinkIcon size={18} />
                    <a href={venue.link} target="_blank" rel="noopener noreferrer">
                      View Website
                    </a>
                  </div>
                )}
              </div>
            </div>
            </section>
          </section>

          {/* Booking Form */}
          <section className="venue-detail-page__booking">
            <div className="venue-detail-page__booking-header">
              <h2>Book This Venue</h2>
              <p>Fill out the form below, then continue to secure payment via Stripe Checkout.</p>
            </div>

            {submitted ? (
              <div className="venue-detail-page__success">
                <CheckCircle size={48} />
                <h3>Booking Request Submitted!</h3>
                <p>
                  Your booking request has been received and is pending approval. You will be
                  notified once it has been reviewed.
                </p>
                <button
                  type="button"
                  className="venue-detail-page__success-link"
                  onClick={() => navigate('/my-venue-bookings')}
                >
                  View My Bookings
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="venue-detail-page__booking-form">
                <div className="venue-detail-page__form-section">
                  <h4>Booking Details</h4>

                  <div className="venue-detail-page__form-group">
                    <label>
                      <Calendar size={16} />
                      Booking Date <span className="required">*</span>
                    </label>
                    <div className="venue-detail-page__calendar" ref={calendarPickerRef}>
                      <button
                        type="button"
                        className="venue-detail-page__calendar-trigger"
                        onClick={() => setIsCalendarOpen((prev) => !prev)}
                        aria-expanded={isCalendarOpen}
                        aria-haspopup="dialog"
                      >
                        <span>{bookingDateLabel}</span>
                      </button>

                      {isCalendarOpen && (
                        <div className="venue-detail-page__calendar-popover" role="dialog" aria-label="Booking date calendar">
                          <div className="venue-detail-page__calendar-header">
                            <button
                              type="button"
                              className="venue-detail-page__calendar-nav"
                              onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                              aria-label="Previous month"
                            >
                              <ChevronLeft size={16} />
                            </button>
                            <strong>
                              {calendarMonth.toLocaleDateString('en-PH', {
                                month: 'long',
                                year: 'numeric',
                              })}
                            </strong>
                            <button
                              type="button"
                              className="venue-detail-page__calendar-nav"
                              onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                              aria-label="Next month"
                            >
                              <ChevronRight size={16} />
                            </button>
                          </div>

                          <div className="venue-detail-page__calendar-weekdays">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                              <span key={day}>{day}</span>
                            ))}
                          </div>

                          <div className="venue-detail-page__calendar-grid">
                            {calendarDays.map((dateItem) => (
                              <button
                                key={dateItem.iso}
                                type="button"
                                className={`venue-detail-page__calendar-day${
                                  dateItem.isCurrentMonth ? '' : ' is-outside'
                                }${dateItem.isSelected ? ' is-selected' : ''}${dateItem.isToday ? ' is-today' : ''}`}
                                disabled={dateItem.isPast}
                                onClick={() => handleBookingDateSelect(dateItem.iso)}
                                aria-label={dateItem.iso}
                              >
                                {dateItem.day}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {bookingForm.bookingDate && (
                    <div className="venue-detail-page__availability">
                      <h4>
                        <Clock size={16} />
                        Time Slot Availability
                      </h4>
                      {loadingAvailability ? (
                        <p className="venue-detail-page__availability-loading">
                          Checking availability...
                        </p>
                      ) : (
                        <>
                          <div className="venue-detail-page__duration-picker">
                            <span className="venue-detail-page__duration-label">How many hours? <span className="required">*</span></span>
                            <div className="venue-detail-page__duration-options">
                              {BOOKING_DURATION_OPTIONS.map((durationOption) => {
                                const isSelected = selectedDurationHours === durationOption;

                                return (
                                  <button
                                    key={durationOption}
                                    type="button"
                                    className={`venue-detail-page__duration-option ${isSelected ? 'is-selected' : ''}`}
                                    onClick={() => handleDurationSelection(durationOption)}
                                    title={`${durationOption} hour booking`}
                                    aria-label={`${durationOption} hours`}
                                  >
                                    {durationOption} hrs
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          {selectedDurationHours && (
                            <>
                              <p className="venue-detail-page__slot-hint">
                                Click one start time. End time and range are auto-selected.
                              </p>
                              <div className="venue-detail-page__time-grid">
                              {TIME_SLOTS.map((slot) => {
                                const occupied = isSlotOccupied(slot.value);
                                const durationConflict = Boolean(selectedDurationHours) && hasDurationOverlapFromSlot(slot.value, selectedDurationHours);
                                const unavailable = occupied || durationConflict;
                                const isStartTime = bookingForm.startTime === slot.value;
                                const isEndTime = bookingForm.endTime === slot.value && !unavailable;
                                const isWithinRange =
                                  bookingForm.startTime &&
                                  bookingForm.endTime &&
                                  (endDayOffset === 0
                                    ? (slot.value > bookingForm.startTime && slot.value < bookingForm.endTime)
                                    : (slot.value > bookingForm.startTime));

                                return (
                                  <button
                                    key={slot.value}
                                    type="button"
                                    className={`venue-detail-page__time-slot ${
                                      unavailable ? 'occupied' : 'available'
                                    } ${durationConflict ? 'duration-conflict' : ''} ${
                                      occupied ? 'already-booked' : ''
                                    } ${isStartTime ? 'selected-start' : ''} ${
                                      isEndTime ? 'selected-end' : ''
                                    } ${isWithinRange ? 'in-range' : ''}`}
                                    onClick={() => handleTimeSlotSelection(slot.value)}
                                    disabled={unavailable}
                                    title={occupied ? 'Already booked' : durationConflict ? 'Unavailable for selected duration' : selectedDurationHours ? 'Select start time' : 'Choose duration first'}
                                    aria-label={`${slot.label}${unavailable ? ' unavailable' : ''}`}
                                  >
                                    {slot.label}
                                  </button>
                                );
                              })}
                              </div>
                              <div className="venue-detail-page__legend">
                                <span className="venue-detail-page__legend-item">
                                  <span className="venue-detail-page__legend-box venue-detail-page__legend-box--available" />
                                  Available
                                </span>
                                <span className="venue-detail-page__legend-item">
                                  <span className="venue-detail-page__legend-box venue-detail-page__legend-box--occupied" />
                                  Occupied
                                </span>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {bookingForm.startTime && (
                    <div className="venue-detail-page__selection-summary">
                      <span>Duration: <strong>{selectedDurationHours ? `${selectedDurationHours} hrs` : '—'}</strong></span>
                      <span>Start: <strong>{formatTime(bookingForm.startTime)}</strong></span>
                      <span>
                        End: <strong>{bookingForm.endTime ? `${formatTime(bookingForm.endTime)}${endDayOffset > 0 ? ' (next day)' : ''}` : 'Select end time'}</strong>
                      </span>
                    </div>
                  )}
                </div>

                {bookingError && (
                  <div className="venue-detail-page__error-message">
                    <AlertCircle size={18} />
                    {bookingError}
                  </div>
                )}

                <div className="venue-detail-page__form-actions">
                  <button
                    type="button"
                    className="venue-detail-page__cancel-button"
                    onClick={() => navigate('/venues')}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="venue-detail-page__submit-button"
                    disabled={submitting}
                  >
                    <CheckCircle size={18} />
                    {submitting ? 'Processing...' : 'Pay Now'}
                  </button>
                </div>
              </form>
            )}
          </section>
        </div>

        {showConfirmModal && (
          <div className="venue-detail-page__modal" role="dialog" aria-modal="true" aria-labelledby="booking-confirmation-title">
            <button
              type="button"
              className="venue-detail-page__modal-backdrop"
              onClick={() => setShowConfirmModal(false)}
              aria-label="Close confirmation modal"
            />
            <div className="venue-detail-page__modal-panel">
              <div className="venue-detail-page__modal-header">
                <h3 id="booking-confirmation-title">Confirm Booking Request</h3>
                <p>Review your details before submitting.</p>
              </div>

              <div className="venue-detail-page__modal-body">
                <div className="venue-detail-page__modal-grid">
                  {confirmationDetails.map((item) => (
                    <div key={item.label}>
                      <span className="venue-detail-page__modal-label">{item.label}</span>
                      <span className="venue-detail-page__modal-value">{item.value}</span>
                    </div>
                  ))}
                </div>

                <div className="venue-detail-page__modal-total">
                  <span>Estimated Total Cost</span>
                  <strong>{formatCurrency(estimatedTotalCost)}</strong>
                </div>
              </div>

              <div className="venue-detail-page__modal-footer">
                <div className="venue-detail-page__modal-footer-right">
                  <button
                    type="button"
                    className="venue-detail-page__modal-cancel"
                    onClick={() => setShowConfirmModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="venue-detail-page__cancel-button"
                    onClick={() => setShowConfirmModal(false)}
                    disabled={submitting}
                  >
                    Edit Details
                  </button>
                  <button
                    type="button"
                    className="venue-detail-page__submit-button"
                    onClick={handleConfirmBooking}
                    disabled={submitting}
                  >
                    {submitting ? 'Processing...' : 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
      <VenueChatbot user={user} onBookingCreated={() => navigate('/my-venue-bookings')} />
    </AppLayout>
  );
}

VenueDetailPage.propTypes = {
  user: PropTypes.object,
  onLogout: PropTypes.func.isRequired,
};
